import type React from "react";
import { useState } from "react";

import type { UserRoleDto } from "@/api/types";
import { Drawer } from "@/components/Drawer/Drawer";
import { ChevronRightIcon } from "@/components/Icons/ChevronRightIcon";
import { ShieldTickIcon } from "@/components/Icons/ShieldTickIcon";
import { PermissionListLayout, PermissionWrapper } from "@/components/Permission/Permission";
import { Accordion } from "@/design-system/Accordion";
import { Button, buttonVariants } from "@/design-system/Button";
import { Checkbox } from "@/design-system/Checkbox";
import { twResolve } from "@/helpers/tw-resolve";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

interface PermissionField {
  key: string;
  label: string;
}

type Permission = Record<string, { name: string; roleType: UserRoleDto["type"] }>;

export interface PermissionSelectorProps<T extends Permission> {
  defaultPermissions: T;
  fields: PermissionField[];
  onSave: (permissions: T) => void;
}

export function PermissionSelector<T extends Permission>({
  defaultPermissions,
  fields,
  onSave,
}: PermissionSelectorProps<T>): React.ReactNode {
  const [isDrawerOpened, drawerHandler] = useBool();
  const [localPermissions, setLocalPermissions] = useState(defaultPermissions);
  const { t } = useTranslation();

  const onOpen = () => {
    setLocalPermissions(defaultPermissions);
    drawerHandler.setTrue();
  };

  const onClickCancel = () => {
    setLocalPermissions(defaultPermissions);
  };

  const onClickSave = () => {
    onSave(localPermissions);
  };

  const onTogglePermission = (roleKey: string, fieldKey: string, checked: boolean) => {
    setLocalPermissions((prev) => ({
      ...prev,
      [roleKey]: {
        ...prev[roleKey],
        [fieldKey]: checked,
      },
    }));
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        className={twResolve(
          buttonVariants({
            variant: "ghostSecondary",
            type: "button",
            fullWidth: true,
            content: "body",
            pressAnimation: false,
          }),
          "max-w-full overflow-visible rounded-lg! border border-border-secondary text-left text-clip whitespace-normal transition-colors hover:border-border-tertiary focus-visible:border-border-tertiary",
        )}
      >
        <span className="flex min-h-control-sm w-full items-center justify-between gap-space-sm p-space-sm">
          <span className="flex items-center gap-space-xs text-text-secondary">
            <ShieldTickIcon />
            <span>{t("component.permission-selector.label")}</span>
          </span>
          <ChevronRightIcon />
        </span>
      </button>
      <Drawer.Root
        title={t("component.permission-selector.title")}
        isOpened={isDrawerOpened}
        onOpenChange={drawerHandler.set}
      >
        <Drawer.Body>
          <Accordion.Root>
            {Object.entries(localPermissions)
              .toSorted(([, a], [, b]) => {
                // Residents first, then sort by name
                if (a.roleType === "resident" && b.roleType !== "resident") {
                  return -1;
                }

                if (a.roleType !== "resident" && b.roleType === "resident") {
                  return 1;
                }

                return a.name.localeCompare(b.name);
              })
              .map(([roleKey, role]) => (
                <Accordion.Item key={roleKey} value={roleKey}>
                  <Accordion.Trigger>{role.name}</Accordion.Trigger>
                  <Accordion.Content>
                    <PermissionListLayout>
                      {fields.map((field) => (
                        <PermissionWrapper key={field.key} layout="vertical">
                          <Checkbox
                            checked={!!(role as Record<string, unknown>)[field.key]}
                            onChange={(e) => onTogglePermission(roleKey, field.key, e.target.checked)}
                            label={field.label}
                            alignTop
                          />
                        </PermissionWrapper>
                      ))}
                    </PermissionListLayout>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
          </Accordion.Root>
        </Drawer.Body>
        <Drawer.Actions>
          <Drawer.Close>
            <Button variant="subtle" tone="primary" onClick={onClickCancel}>
              {t("common.action.cancel")}
            </Button>
          </Drawer.Close>
          <Drawer.Close>
            <Button variant="filled" onClick={onClickSave}>
              {t("common.action.set")}
            </Button>
          </Drawer.Close>
        </Drawer.Actions>
      </Drawer.Root>
    </>
  );
}
